import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { BarGrid } from '@/components/charts/BarGrid'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order: Strongly Agree → Agree → Neutral/Unsure → Disagree → Strongly Disagree (case-insensitive)
// Note: Data has inconsistent neutral labels: "Neutral / Unsure", "Neutral /Unsure", "Neutral Unsure"
const AGREE_RESPONSE_ORDER = ['strongly agree', 'somewhat agree', 'agree', 'neutral / unsure', 'neutral /unsure', 'neutral unsure', 'unsure', 'neutral', 'disagree', 'somewhat disagree', 'strongly disagree']

// Sort responses according to agree/disagree order (Unsure in the middle) - case-insensitive matching
const sortAgreeResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = AGREE_RESPONSE_ORDER.indexOf(a.label.toLowerCase())
    const bIdx = AGREE_RESPONSE_ORDER.indexOf(b.label.toLowerCase())
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// Legend labels (simplified for display)
const LEGEND_LABELS = ['Strongly Agree', 'Somewhat Agree', 'Neutral / Unsure', 'Somewhat Disagree', 'Strongly Disagree']

const VALUES_ISSUES_QUESTIONS = [
  { id: 'Agree_WealthyInfluence', label: 'Corporations and the wealthy have too much influence over politicians and the government.' },
  { id: 'Agree_StricterPollutionPenalties', label: 'I support stricter penalties for industrial polluters, including jail-time for executives.' },
  { id: 'Agree_RespectIndigenousRights', label: 'Canada needs to respect Indigenous rights and title.' },
  { id: 'Agree_Traitor51stState', label: "Any Canadian who supports Canada becoming America's 51st state is a traitor." },
  { id: 'Agree_IndustryEnvDamage', label: 'Industry is in the process of destroying everything on earth because of its lack of consideration for the environment.' },
  { id: 'Agree_CutCorpTaxes', label: 'Cutting corporate taxes and red tape is good for jobs and the economy.' },
  { id: 'Agree_IndigenousInfluence', label: 'Indigenous nations have far too much influence over governments in this country.' },
  { id: 'Agree_VotingPointless', label: 'There is no point in voting, nothing is going to change.' },
] as const

// Open-ended questions V1 and V2 from topline
const OPEN_ENDED_QUESTIONS = [
  {
    id: 'OpenEnded_BestThingRegion',
    title: 'Best Thing About Your Region (UNAIDED)',
    text: 'In your opinion, what is the best thing about your home region compared to other regions of Canada and the world?',
  },
  {
    id: 'OpenEnded_LocalConcerns',
    title: 'Local Issues and Concerns (UNAIDED)',
    text: 'When you think of issues or problems that affect the region in which you live, what upsets you most?',
  },
] as const

// Config for Best Thing About Region
const BEST_THING_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Natural environment', shortLabel: 'Nature', color: '#1a472a' },
  { label: 'Recreation and leisure', shortLabel: 'Recreation', color: '#2d6a2f' },
  { label: 'Quiet', color: '#59a14f' },
  { label: 'Community and friendliness', shortLabel: 'Community', color: '#1e3a5f' },
  { label: 'Family and community ties', shortLabel: 'Family ties', color: '#2c5985' },
  { label: 'Cultural diversity', shortLabel: 'Diversity', color: '#4e79a7' },
  { label: 'Convenience and accessibility', shortLabel: 'Convenience', color: '#b33c00' },
  { label: 'Safety and security', shortLabel: 'Safety', color: '#d45500' },
  { label: 'Rural/small town life', shortLabel: 'Rural life', color: '#f28e2c' },
  { label: 'Cost of living', shortLabel: 'Cost', color: '#5c2d51' },
  { label: 'Economic opportunity', shortLabel: 'Economy', color: '#7b4173' },
  { label: 'Healthcare', color: '#2d5f5a' },
  { label: 'Food', color: '#499894' },
  { label: 'No advantages', shortLabel: 'No advantages', color: '#6b7280' },
  { label: 'Prefer local politicians', shortLabel: 'Local politics', color: '#9ca3af' },
]

// Config for Local Issues and Concerns
const LOCAL_CONCERNS_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Cost of living', shortLabel: 'Cost', color: '#b33c00' },
  { label: 'Affordable housing', shortLabel: 'Housing', color: '#d45500' },
  { label: 'Employment and economy', shortLabel: 'Jobs', color: '#f28e2c' },
  { label: 'Crime and safety', shortLabel: 'Crime', color: '#7a0c0c' },
  { label: 'Homeless people', shortLabel: 'Homeless', color: '#a01a1c' },
  { label: 'Inequality and discrimination', shortLabel: 'Inequality', color: '#e15759' },
  { label: 'Too much immigration', shortLabel: 'Immigration', color: '#ff9d9a' },
  { label: 'Transportation and infrastructure', shortLabel: 'Transport', color: '#1e3a5f' },
  { label: 'Healthcare and social services', shortLabel: 'Healthcare', color: '#2c5985' },
  { label: 'Environment and climate', shortLabel: 'Environment', color: '#2d6a2f' },
  { label: 'Government and politics', shortLabel: 'Government', color: '#7b4173' },
  { label: 'Neighborhood cohesion', shortLabel: 'Neighbors', color: '#499894' },
  { label: 'Need more homeless support', shortLabel: 'Support homeless', color: '#374151' },
]

const OPEN_ENDED_CONFIGS: Record<string, Array<{ label: string; shortLabel?: string; color: string }>> = {
  'OpenEnded_BestThingRegion': BEST_THING_CONFIG,
  'OpenEnded_LocalConcerns': LOCAL_CONCERNS_CONFIG,
}

export function ValuesAndIssues() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Sort questions by agreement level (Strongly Agree + Somewhat Agree) - highest first
  const sortedQuestions = useMemo(() => {
    if (!data) return VALUES_ISSUES_QUESTIONS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...VALUES_ISSUES_QUESTIONS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const getAgreeScore = (qData: typeof aData) => {
        if (!qData) return 0
        const stronglyAgree = qData.responses.find(r => r.label === 'Strongly Agree' || r.label === 'Strongly agree')?.percentage || 0
        const somewhatAgree = qData.responses.find(r => r.label === 'Somewhat Agree' || r.label === 'Somewhat agree' || r.label === 'Agree')?.percentage || 0
        return stronglyAgree + somewhatAgree
      }

      return getAgreeScore(bData) - getAgreeScore(aData)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Values and Issues</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Please say whether you AGREE or DISAGREE with the following statements.
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('likert5', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedQuestions.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAgreeResponses(row.data!.responses)}
                            scaleType="likert5"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Open-ended questions V1 and V2 */}
      {data && OPEN_ENDED_QUESTIONS.map(({ id, title, text }) => {
        const question = data.questions[id]
        if (!question) return null

        const config = OPEN_ENDED_CONFIGS[id] || []
        const sampleData = question.data[buildFilterKey('total', 'total')]
        const dataLabels = new Set(sampleData?.responses?.map(r => r.label) || [])

        const columns = config
          .filter(c => dataLabels.has(c.label))
          .map(c => ({
            id: c.label,
            label: c.label,
            shortLabel: c.shortLabel,
            color: c.color,
          }))

        const rows = breakValues.map(value => {
          const filterKey = buildFilterKey(
            'total',
            selectedBreak,
            isBreakActive ? value : undefined,
            selectedProvince
          )
          let questionData = question.data[filterKey]

          if (!questionData && selectedProvince) {
            const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
            questionData = question.data[fallbackKey]
          }

          if (!questionData) return null

          const values: Record<string, number> = {}
          questionData.responses.forEach(r => {
            values[r.label] = r.percentage
          })

          return {
            id: value,
            label: isBreakActive ? getDisplayName(value) : 'National Sample',
            values,
          }
        }).filter((row): row is NonNullable<typeof row> => row !== null)

        if (rows.length === 0 || columns.length === 0) return null

        return (
          <div key={id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
            <BarGrid
              title={title}
              description={text}
              columns={columns}
              rows={rows}
              colorIntensity={false}
            />
          </div>
        )
      })}
    </div>
  )
}
